import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert as MuiAlert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  Grid,
  Skeleton as MuiSkeleton,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Delete,
  Add,
  Refresh,
  LocalOffer,
  CheckCircle,
  Cancel,
  ContentCopy,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import colors from '../design-system/colors';
import shadows from '../design-system/shadows';
import spacing from '../design-system/spacing';
import { Button, Card, Stack } from '../design-system';
import usePageTitle from '../hooks/usePageTitle';
import {
  getPromoCodes,
  createPromoCode,
  updatePromoCode,
  togglePromoCode,
  deletePromoCode,
  PROMO_DURATION_OPTIONS,
  PROMO_LICENSE_OPTIONS,
  getPromoLicenseLabel,
} from '../services/promoCodesService';

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return isNaN(d) ? '—' : d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const toDateInputValue = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const EMPTY_FORM = {
  code: '',
  discountPercent: 0,
  durationMonths: 1,
  maxUses: '',
  expiresAt: '',
  targetPlanKey: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

const AdminPromoCodesPageModern = () => {
  const navigate = useNavigate();
  usePageTitle('Gestión de Códigos Promocionales');

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');

  // Edit limits dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ maxUses: '', expiresAt: '' });
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPromoCodes();
      setCodes(data);
    } catch (err) {
      setAlert({ severity: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = codes.filter((c) =>
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  // ── create ──

  const handleCreateOpen = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setCreateOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFormError('');
  };

  const handleCreate = async () => {
    setFormError('');
    setCreating(true);
    try {
      await createPromoCode({
        code: form.code,
        discountPercent: form.discountPercent,
        durationMonths: form.durationMonths,
        maxUses: form.maxUses !== '' ? form.maxUses : null,
        expiresAt: form.expiresAt || null,
        targetPlanKey: form.targetPlanKey || null,
      });
      setCreateOpen(false);
      setAlert({ severity: 'success', message: 'Código creado correctamente' });
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // ── toggle ──

  const handleToggle = async (id, current) => {
    try {
      await togglePromoCode(id, !current);
      setCodes((prev) => prev.map((c) => (c.id === id ? { ...c, active: !current } : c)));
    } catch (err) {
      setAlert({ severity: 'error', message: err.message });
    }
  };

  // ── edit limits ──

  const handleEditOpen = (promoCode) => {
    setEditTarget(promoCode);
    setEditForm({
      maxUses: promoCode.maxUses ?? '',
      expiresAt: toDateInputValue(promoCode.expiresAt),
    });
    setEditError('');
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setEditError('');
    setEditing(true);
    try {
      const updated = await updatePromoCode(editTarget.id, {
        maxUses: editForm.maxUses,
        expiresAt: editForm.expiresAt,
      });
      setCodes((prev) => prev.map((code) => (
        code.id === editTarget.id ? { ...code, ...updated } : code
      )));
      setEditTarget(null);
      setAlert({ severity: 'success', message: 'Límites del código actualizados' });
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditing(false);
    }
  };

  // ── delete ──

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePromoCode(deleteTarget.id);
      setDeleteTarget(null);
      setAlert({ severity: 'success', message: 'Código eliminado' });
      load();
    } catch (err) {
      setAlert({ severity: 'error', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  // ── copy ──

  const handleCopy = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setAlert({ severity: 'info', message: `Código "${code}" copiado al portapapeles` });
  };

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <Box className="wp-page" component="section" sx={{ flex: 1, width: '100%', p: { xs: `${spacing.md}px`, md: `${spacing.xl}px` }, maxWidth: 1200, mx: 'auto', bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Header */}
      <Stack className="wp-heading" direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} aria-label="volver">
          <ArrowBack />
        </IconButton>
        <LocalOffer sx={{ color: colors.primary[500], fontSize: 32 }} />
        <Box>
          <Typography component="h1" variant="h5" fontWeight={700}>
            Códigos Promocionales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crea y gestiona códigos que otorgan tiempo de uso o descuentos a los usuarios al registrarse
          </Typography>
        </Box>
      </Stack>

      {alert && (
        <MuiAlert
          severity={alert.severity}
          onClose={() => setAlert(null)}
          sx={{ mb: `${spacing.md}px` }}
        >
          {alert.message}
        </MuiAlert>
      )}

      {/* Toolbar */}
      <Stack className="wp-toolbar" direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar código..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 220px', minWidth: 0, maxWidth: 320 }}
        />
        <Tooltip title="Actualizar">
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateOpen}
        >
          Nuevo código
        </Button>
      </Stack>

      {/* Table */}
      <Card sx={{ boxShadow: shadows.md }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                {['Código', 'Licencia', 'Duración', 'Descuento', 'Usos', 'Expira', 'Estado', 'Creado', 'Acciones'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <TableCell key={j}>
                          <MuiSkeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : filtered.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {search ? 'No se encontraron códigos con ese criterio' : 'No hay códigos promocionales'}
                      </TableCell>
                    </TableRow>
                  )
                : filtered.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{ opacity: c.active ? 1 : 0.5 }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                            {c.code}
                          </Typography>
                          <Tooltip title="Copiar">
                            <IconButton size="small" onClick={() => handleCopy(c.code)}>
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getPromoLicenseLabel(c.targetPlanKey)}
                          color={c.targetPlanKey ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={PROMO_DURATION_OPTIONS.find((d) => d.value === c.durationMonths)?.label || `${c.durationMonths}m`}
                          sx={{ backgroundColor: colors.info[50], color: colors.info[700] }}
                        />
                      </TableCell>
                      <TableCell>
                        {c.discountPercent > 0
                          ? <Chip size="small" label={`${c.discountPercent}%`} sx={{ backgroundColor: colors.success[50], color: colors.success[700] }} />
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {c.usedCount ?? 0}{c.maxUses != null ? ` / ${c.maxUses}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(c.expiresAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={c.active ? 'Desactivar' : 'Activar'}>
                          <Switch
                            size="small"
                            checked={!!c.active}
                            onChange={() => handleToggle(c.id, c.active)}
                          />
                        </Tooltip>
                        {c.active
                          ? <CheckCircle sx={{ fontSize: 16, color: colors.success[500], ml: 0.5, verticalAlign: 'middle' }} />
                          : <Cancel sx={{ fontSize: 16, color: colors.error[400], ml: 0.5, verticalAlign: 'middle' }} />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(c.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Editar usos y expiración">
                            <IconButton size="small" onClick={() => handleEditOpen(c)} color="primary">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => setDeleteTarget(c)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Nuevo Código Promocional</DialogTitle>
        <DialogContent>
          <Stack spacing={`${`${spacing.md}px`}px`} sx={{ mt: 1 }}>
            {formError && (
              <MuiAlert severity="error" onClose={() => setFormError('')}>
                {formError}
              </MuiAlert>
            )}

            <TextField
              label="Código"
              value={form.code}
              onChange={handleFormChange('code')}
              fullWidth
              helperText="Se convertirá a mayúsculas automáticamente"
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />

            <FormControl fullWidth>
              <InputLabel>Licencia aplicable</InputLabel>
              <Select
                value={form.targetPlanKey}
                label="Licencia aplicable"
                onChange={handleFormChange('targetPlanKey')}
              >
                {PROMO_LICENSE_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'any'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                El código solo activará o ampliará esta licencia. Usa “Acceso completo” para todas las funcionalidades.
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Duración</InputLabel>
              <Select
                value={form.durationMonths}
                label="Duración"
                onChange={handleFormChange('durationMonths')}
              >
                {PROMO_DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" gutterBottom>
                Descuento: <strong>{form.discountPercent}%</strong>
                {form.discountPercent === 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    (acceso gratuito sin descuento)
                  </Typography>
                )}
              </Typography>
              <Slider
                value={form.discountPercent}
                onChange={(_, val) => {
                  setForm((prev) => ({ ...prev, discountPercent: val }));
                  setFormError('');
                }}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Máx. usos (vacío = ilimitado)"
                  type="number"
                  value={form.maxUses}
                  onChange={handleFormChange('maxUses')}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Fecha de expiración del código"
                  type="date"
                  value={form.expiresAt}
                  onChange={handleFormChange('expiresAt')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Opcional"
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creando...' : 'Crear código'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Limits Dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Editar {editTarget?.code}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={`${`${spacing.md}px`}px`} sx={{ mt: 1 }}>
            {editError && (
              <MuiAlert severity="error" onClose={() => setEditError('')}>
                {editError}
              </MuiAlert>
            )}
            <TextField
              label="Máx. usos (vacío = ilimitado)"
              type="number"
              value={editForm.maxUses}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, maxUses: event.target.value }));
                setEditError('');
              }}
              inputProps={{ min: 1 }}
              helperText={`${editTarget?.usedCount ?? 0} usos realizados`}
              fullWidth
            />
            <TextField
              label="Fecha de expiración del código"
              type="date"
              value={editForm.expiresAt}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, expiresAt: event.target.value }));
                setEditError('');
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Vacío = sin expiración"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setEditTarget(null)} disabled={editing}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar código</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar el código{' '}
            <strong>{deleteTarget?.code}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPromoCodesPageModern;
